import { SelectMenu, Button, Accordion } from '@scale/scaleui';
import { InteractionStepType } from '@scale/shared/types/chat';
import BooleanInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/BooleanInputBuilder';
import NumberInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/NumberInputBuilder';
import JSONInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/JSONInputBuilder';
import {
  DEFAULT_PROCESS_CHUNKING_STRATEGY,
  DEFAULT_RATING_BUTTONS,
  DEFAULT_FIELDS_TO_COLLECT,
  DEFAULT_SUB_CHUNKING_STRATEGY,
  DEFAULT_SUB_CHUNKING_STRATEGY_PARAMS,
  TOOLS,
} from './types';
import {
  ProcessChunkingStrategy,
  ProcessChunkingStrategyMap,
  SubProcessChunkingStrategyMap,
} from './chunking';
import { ConditionalStepConfig } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/configs';
import { StepConfigurationComponentProps } from '../stepDescriptor/types';

export function ExperimentalToolUseConfigurationComponent({
  params: currentParams,
  setParams: setCurrentParams,
  previousParams,
  taskParams,
}: StepConfigurationComponentProps<InteractionStepType.ExperimentalToolUse>) {
  const strategyName = (currentParams.params.process_chunking_strategy ||
    DEFAULT_PROCESS_CHUNKING_STRATEGY) as ProcessChunkingStrategy;
  return (
    // TODO clean this page up into tabs like ModelResponseSelectorConfigurationComponent
    <div className="flex flex-col gap-4">
      {/* select a value from ProcessChunkingStrategyMap */}
      <SelectMenu
        label={`Select Process Chunking Strategy`}
        options={Object.keys(ProcessChunkingStrategyMap).map(key => ({
          id: key,
          name: key,
        }))}
        selectedOptionId={
          currentParams.params.process_chunking_strategy || DEFAULT_PROCESS_CHUNKING_STRATEGY
        }
        setSelected={id =>
          setCurrentParams({
            ...currentParams,
            params: { ...currentParams.params, process_chunking_strategy: id },
          })
        }
      />
      {ProcessChunkingStrategyMap[strategyName]?.length > 1 && (
        <JSONInputBuilder
          name={'Process Chunking Strategy Params'}
          existingValue={currentParams.params.process_chunking_strategy_params}
          updateProperty={(value: any) => {
            setCurrentParams({
              ...currentParams,
              params: { ...currentParams.params, process_chunking_strategy_params: value },
            });
          }}
          required={false}
        />
      )}

      {
        <SelectMenu
          label={`Select Tools`}
          options={TOOLS.map(key => ({
            id: key,
            name: key,
          }))}
          multiple={true}
          selectedOptionId={currentParams.params.tools || []}
          setSelected={id =>
            setCurrentParams({
              ...currentParams,
              params: { ...currentParams.params, tools: id },
            })
          }
        />
      }
      <NumberInputBuilder
        name={'Min Negative Ratings'}
        existingValue={currentParams.params.min_negative_ratings}
        updateProperty={(value: number) => {
          setCurrentParams({
            ...currentParams,
            params: { ...currentParams.params, min_negative_ratings: value },
          });
        }}
      />

      {/* tool to create and configure the buttons (with color: "danger" | "neutral" | "success") */}
      {/* sets this as rating_buttons */}
      {/* defaults to DEFAULT_RATING_BUTTONS */}
      {/* and render the buttons below it */}
      <JSONInputBuilder
        name={'Rating Buttons'}
        existingValue={currentParams.params.rating_buttons || DEFAULT_RATING_BUTTONS}
        updateProperty={(value: any) => {
          setCurrentParams({
            ...currentParams,
            params: { ...currentParams.params, rating_buttons: value },
          });
        }}
        required={false}
      />
      <div className="flex flex-row gap-4">
        {currentParams.params.rating_buttons?.map((button: any) => (
          <Button key={button.label} variant={button.color} size="sm">
            {button.label} ({button.value})
          </Button>
        ))}
      </div>

      <JSONInputBuilder
        name={'Fields to Collect'}
        existingValue={currentParams.params.fields_to_collect || DEFAULT_FIELDS_TO_COLLECT}
        updateProperty={(value: any) => {
          setCurrentParams({
            ...currentParams,
            params: { ...currentParams.params, fields_to_collect: value },
          });
        }}
        required={false}
      />
      <div className="flex flex-col gap-4">
        {currentParams.params.fields_to_collect?.map((field: any) => (
          <div key={field.fieldId} className="flex flex-row justify-between items-center">
            <span>
              {field.label} ({field.type})
            </span>
            <span className="flex flex-row gap-1 items-center">
              Render for values:
              {field.collectForRatingValues.map((ratingValue: number) => (
                <span key={ratingValue}>{ratingValue}</span>
              ))}
            </span>
          </div>
        ))}
      </div>
      {/* TODO make this easier to configure and add warnings if misconfigured (e.g. render for values don't match rating buttons) */}

      <JSONInputBuilder
        name={'End After Rating IDs'}
        existingValue={currentParams.params.end_after_rating_ids || [-1]}
        updateProperty={(value: any) => {
          setCurrentParams({
            ...currentParams,
            params: { ...currentParams.params, end_after_rating_ids: value },
          });
        }}
        required={false}
      />

      <BooleanInputBuilder
        name={'Allow user to skip entire turn'}
        existingValue={currentParams.params.allow_skip ?? false}
        updateProperty={(value: any) => {
          setCurrentParams({
            ...currentParams,
            params: { ...currentParams.params, allow_skip: value },
          });
        }}
        required={false}
      />

      <BooleanInputBuilder
        name={'Disable the ability to edit chunks'}
        existingValue={currentParams.params.disable_edit_chunks ?? false}
        updateProperty={(value: any) => {
          setCurrentParams({
            ...currentParams,
            params: { ...currentParams.params, disable_edit_chunks: value },
          });
        }}
        required={false}
      />

      <BooleanInputBuilder
        name={'Allow edits without regenerating remaining conversation'}
        existingValue={currentParams.params.enable_edit_without_reroll ?? false}
        updateProperty={(value: any) => {
          setCurrentParams({
            ...currentParams,
            params: { ...currentParams.params, enable_edit_without_reroll: value },
          });
        }}
        required={false}
        hint="If enabled, users can edit prior turns without resetting subsequent turns"
      />

      {currentParams.params.end_after_rating_ids &&
        currentParams.params.end_after_rating_ids.length > 0 && (
          <>
            <BooleanInputBuilder
              name={'Re-request model using rewrites'}
              existingValue={currentParams.params.re_request_model ?? true}
              updateProperty={(value: any) => {
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, re_request_model: value },
                });
              }}
              required={false}
            />
          </>
        )}
      <Accordion
        className="w-full"
        header="Advanced Configuration"
        target={
          <div className="flex flex-col gap-4">
            <BooleanInputBuilder
              name={'Use Sub Chunking Strategy (Beta)'}
              existingValue={currentParams.params.use_sub_chunking_strategy ?? false}
              updateProperty={(value: any) => {
                setCurrentParams({
                  ...currentParams,
                  params: { ...currentParams.params, use_sub_chunking_strategy: value },
                });
              }}
              required={false}
            />
            {currentParams.params.use_sub_chunking_strategy && (
              <SelectMenu
                label={`Select Sub Chunking Strategy`}
                options={Object.keys(SubProcessChunkingStrategyMap).map(key => ({
                  id: key,
                  name: key,
                }))}
                selectedOptionId={
                  currentParams.params.sub_chunking_strategy || DEFAULT_SUB_CHUNKING_STRATEGY
                }
                setSelected={id =>
                  setCurrentParams({
                    ...currentParams,
                    params: { ...currentParams.params, sub_chunking_strategy: id },
                  })
                }
              />
            )}
            {currentParams.params.use_sub_chunking_strategy && (
              <JSONInputBuilder
                name={'Sub Chunking Strategy Params'}
                existingValue={
                  currentParams.params.sub_chunking_strategy_params ||
                  DEFAULT_SUB_CHUNKING_STRATEGY_PARAMS
                }
                updateProperty={(value: any) => {
                  setCurrentParams({
                    ...currentParams,
                    params: { ...currentParams.params, sub_chunking_strategy_params: value },
                  });
                }}
                required={false}
              />
            )}
          </div>
        }
      />

      <ConditionalStepConfig
        currentParams={currentParams}
        setCurrentParams={setCurrentParams}
        previousParams={previousParams}
        taskParams={taskParams}
      />
      <div className="flex my-4 justify-end">
        <Button disabled={true}>Changes Autosaved</Button>
      </div>
    </div>
  );
}
